import { useCallback, useRef, useState } from 'react'

/**
 * Previsualización fiel de la descripción de una ficha de producto.
 *
 * Las fichas v2 (plantilla AI-ready, `jvs-product--v2`) no llevan CSS inline: sus estilos
 * viven en `themes/dobuss/assets/css/jvs-product.css` de la tienda. Si el HTML se pinta
 * directamente en la app (dangerouslySetInnerHTML + clases `prose`), sale sin maquetar y
 * parece mucho peor que una ficha v1 antigua, que sí lleva `<style>` + estilos inline.
 *
 * Se renderiza dentro de un iframe que enlaza la hoja de estilos real de la tienda, así que
 * la previsualización coincide con lo que verá el cliente. El iframe además aísla el
 * `<style>` global que traen las fichas v1 (antes se inyectaba en toda la página de la app).
 */
const STORE_PRODUCT_CSS = 'https://www.jvs-informatica.com/themes/dobuss/assets/css/jvs-product.css'

const BASE_CSS = `
html,body{margin:0;padding:16px;background:#fff;color:#1f2937;
  font-family:"Segoe UI",system-ui,-apple-system,Roboto,Helvetica,Arial,sans-serif;
  font-size:14px;line-height:1.6;-webkit-font-smoothing:antialiased}
img{max-width:100%;height:auto}
table{max-width:100%}
`

interface Props {
  html: string
  /** Altura máxima en píxeles antes de hacer scroll dentro del iframe */
  maxHeight?: number
  className?: string
  title?: string
}

export default function JvsProductHtmlPreview({
  html,
  maxHeight = 500,
  className = '',
  title = 'Previsualización de la ficha',
}: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(240)

  const srcDoc = `<!doctype html><html lang="es"><head><meta charset="utf-8">` +
    `<link rel="stylesheet" href="${STORE_PRODUCT_CSS}">` +
    `<style>${BASE_CSS}</style></head><body>${html || ''}</body></html>`

  const resize = useCallback(() => {
    const doc = iframeRef.current?.contentDocument
    if (!doc?.body) return
    const h = Math.max(doc.body.scrollHeight, doc.documentElement?.scrollHeight || 0)
    setHeight(Math.min(h + 8, maxHeight))
  }, [maxHeight])

  const onLoad = useCallback(() => {
    resize()
    // La hoja de estilos de la tienda llega por red: recalcular al aplicarse.
    window.setTimeout(resize, 300)
    window.setTimeout(resize, 1200)
  }, [resize])

  return (
    <iframe
      ref={iframeRef}
      title={title}
      srcDoc={srcDoc}
      onLoad={onLoad}
      className={`w-full block bg-white border-0 ${className}`}
      style={{ height }}
    />
  )
}
